"use client";

import { useState, useMemo, useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { User, AlertTriangle, ShieldAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { formatPrice } from "@/lib/utils";
import { useScheduledCountdown } from "@/hooks/use-scheduled-countdown";
import { isHeicUrl, convertHeicUrlToJpeg } from "@/lib/image-url-helper";
import { resolveStorageUrl } from "@/lib/storage/public-url";
import { getAvatarUrl } from "@/lib/getAvatarUrl";
import { SQUARE_SM } from "@/lib/images/presets";
import { useMediaQuery } from "@/hooks/use-media-query";

interface Props {
  listingId:   string;
  title:       string;
  price:       string;
  currency?:   string | null;
  // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: not rendered by this card;
  // optional to allow producers that no longer carry free-text `location`.
  location?:   string;
  image_urls:  string | string[];
  description: string;
  condition:   string;
  grade:       string;
  created_at:  string;
  username?:   string;
  avatar_url?: string | null;
  stock?:      number;
  scheduledPublishAt?: string | null;
  onClick?:    () => void;
  alwaysShowOverlay?: boolean;
  disableHoverOverlay?: boolean;
  interactive?: boolean;
  sellerBanned?: boolean;
  cornerTypeBadgeLabel?: string;
  pricePosition?: "bottom-right" | "bottom-left";
  imageLoading?: "lazy" | "eager";
}

export default function SimpleListingCard({
  listingId,
  title,
  price,
  currency,
  location,
  image_urls,
  description,
  condition,
  grade,
  created_at,
  username = "Unknown User",
  avatar_url,
  stock = Infinity,
  scheduledPublishAt = null,
  onClick,
  alwaysShowOverlay = false,
  disableHoverOverlay = false,
  interactive = true,
  sellerBanned = false,
  cornerTypeBadgeLabel,
  pricePosition = "bottom-right",
  imageLoading = "lazy",
}: Props) {
  const router = useRouter();
  const tConditions = useTranslations("conditions");
  const [hovered, setHovered] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [displayImageSrc, setDisplayImageSrc] = useState<string>("");
  const [isConverting, setIsConverting] = useState(false);
  const blobUrlRef = useRef<string | null>(null);
  const canHover = useMediaQuery("(hover: hover) and (pointer: fine)");

  // Check if this is a scheduled listing that should be blurred (with real-time countdown check)
  const { isScheduled } = useScheduledCountdown(scheduledPublishAt);

  // click handler : soit ton onClick, soit navigation par défaut
  const handleClick = () => {
    if (!interactive) return;
    if (onClick) return onClick();
    router.push(`/listings/${listingId}`);
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    // If it's a middle click or ctrl+click, let the browser handle it naturally
    if (e.button === 1 || e.ctrlKey || e.metaKey) {
      return; // Let browser open in new tab
    }
    
    // For regular clicks, prevent default navigation and use our custom handler
    e.preventDefault();
    handleClick();
  };

  // normalize images array
  const images = useMemo(() => {
    if (Array.isArray(image_urls)) return image_urls;
    try {
      return JSON.parse(image_urls) as string[];
    } catch {
      return [];
    }
  }, [image_urls]);

  // Resolve a bucket-relative storage path before <Image> and before
  // convertHeicUrlToJpeg() fetches it. @see lib/storage/url.ts
  const firstImage = resolveStorageUrl(images[0] ?? "/placeholder.svg");
  const isHeic = isHeicUrl(firstImage);
  
  // Convert HEIC URLs to JPEG on load
  useEffect(() => {
    if (isHeic && typeof window !== 'undefined' && firstImage) {
      setIsConverting(true);
      convertHeicUrlToJpeg(firstImage)
        .then((blobUrl) => {
          // Cleanup previous blob URL if any
          if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
          }
          blobUrlRef.current = blobUrl;
          setDisplayImageSrc(blobUrl);
          setIsConverting(false);
        })
        .catch((error) => {
          const errorMessage = error instanceof Error ? error.message : String(error);
          console.error('Failed to convert HEIC in SimpleListingCard:', {
            url: firstImage,
            error: errorMessage,
            errorObject: error
          });
          // Fallback to original - browser may handle it or show broken image
          setDisplayImageSrc(firstImage);
          setIsConverting(false);
        });
    } else {
      setDisplayImageSrc(firstImage);
    }
    
    // Cleanup blob URL on unmount or when src changes
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [firstImage, isHeic]);
  
  const conditionKeyMap: Record<string, string> = { new: "new", "pre-owned": "preOwned", preowned: "preOwned", custom: "custom" }
  const conditionKey = conditionKeyMap[condition.toLowerCase()] || condition
  const prettyCondition = tConditions.has(conditionKey as any) ? tConditions(conditionKey as any) : condition.charAt(0).toUpperCase() + condition.slice(1);
  const isSoldOut = stock === 0;

  const listingUrl = `/listings/${listingId}`;
  const isClickable = interactive && !isSoldOut && !sellerBanned;
  const shouldShowOverlay = alwaysShowOverlay || (!disableHoverOverlay && canHover && hovered);

  const typeBadgeClass =
    cornerTypeBadgeLabel === "Auction"
      ? "border-0 bg-amber-500 text-black shadow-sm"
      : cornerTypeBadgeLabel === "Buy now"
        ? "border-0 bg-emerald-600 text-white shadow-sm"
        : cornerTypeBadgeLabel === "Offers"
          ? "border-0 bg-violet-600 text-white shadow-sm"
          : cornerTypeBadgeLabel === "Pre-order"
            ? "border-0 bg-sky-600 text-white shadow-sm"
            : cornerTypeBadgeLabel === "Scheduled"
              ? "border-0 bg-orange-600 text-white shadow-sm"
              : "border-0 bg-muted text-foreground shadow-sm";

  return (
    <a
      href={isClickable ? listingUrl : undefined}
      onClick={isClickable ? handleLinkClick : (e) => e.preventDefault()}
      aria-disabled={!isClickable}
      className="block no-underline"
    >
      <div
        className={`relative isolate overflow-hidden rounded-3xl aspect-square ${isClickable ? 'cursor-pointer' : 'cursor-default pointer-events-none'} ${isSoldOut ? 'opacity-75' : ''}`}
        onMouseEnter={canHover ? () => setHovered(true) : undefined}
        onMouseLeave={canHover ? () => setHovered(false) : undefined}
      >
      {/* background image with skeleton */}
      <div className="absolute inset-0 bg-muted z-0">
        {(isConverting || !imgLoaded) && (
          <Skeleton
            className="absolute inset-0 bg-muted dark:bg-white/5 animate-none"
          />
        )}
        {displayImageSrc ? (
          <Image
            src={displayImageSrc}
            alt={title}
            fill
            className={`object-cover transition-opacity duration-300 ${(imgLoaded && !isConverting) ? 'opacity-100' : 'opacity-0'} ${
              isScheduled ? 'blur-md brightness-75' : ''
            }`}
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
            loading={imageLoading}
            onLoadingComplete={() => setImgLoaded(true)}
            priority={false}
            onError={() => {
              setImgLoaded(true);
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-muted" />
        )}
        
        {sellerBanned && (
          <div className="absolute inset-0 bg-black/70 dark:bg-black/80 z-20 flex items-center justify-center">
            <div className="text-white text-xs font-medium text-center px-2">
              <ShieldAlert className="h-4 w-4 mx-auto mb-1" />
              Banished to the Phantom Zone
            </div>
          </div>
        )}
        {!sellerBanned && isSoldOut && (
          <div className="absolute inset-0 bg-black/70 dark:bg-black/80 z-20 flex items-center justify-center">
            <div className="text-white text-xs font-medium text-center px-2">
              <AlertTriangle className="h-4 w-4 mx-auto mb-1" />
              Gone Reduced to Atoms
            </div>
          </div>
        )}
      </div>

      {cornerTypeBadgeLabel ? (
        <div className="pointer-events-none absolute left-2 top-2 z-30 max-w-[calc(100%-1rem)]">
          <span
            className={`inline-block truncate rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${typeBadgeClass}`}
          >
            {cornerTypeBadgeLabel}
          </span>
        </div>
      ) : null}

      <div
        className={`pointer-events-none absolute z-30 ${
          pricePosition === "bottom-left" ? "bottom-2 left-2" : "bottom-2 right-2"
        }`}
      >
        <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-black shadow-sm dark:text-black">
          {formatPrice(price, currency || "USD")}
        </span>
      </div>

      {/* avatar + username — hover only */}
      <div className={`absolute top-0 left-0 m-3 flex items-center space-x-2 ${shouldShowOverlay ? 'opacity-100' : 'opacity-0'} transition-opacity duration-200 z-20 max-w-[calc(100%-1.5rem)]`}>
        <div className="relative w-8 h-8 rounded-full overflow-hidden bg-white/90 dark:bg-muted border-2 border-white/50 flex items-center justify-center shrink-0">
          {avatar_url ? (
            <Image
              // `w-8 h-8` box -> SQUARE_SM. `quality` dropped: the preset
              // already encodes it, and 100 on a pre-compressed source only
              // buys a second billed cache key (handoff §2, §5).
              src={getAvatarUrl(avatar_url, SQUARE_SM) ?? ""}
              alt={username}
              fill
              sizes="32px"
              className="object-cover"
            />
          ) : (
            <User className="h-4 w-4 text-gray-600 dark:text-muted-foreground" />
          )}
        </div>
        <span className="text-xs font-bold text-white truncate min-w-0">
          {username}
        </span>
      </div>

      {/* bottom overlay — hover only */}
      <div
        className={`absolute bottom-0 left-0 right-0 p-3 flex flex-col justify-end
                   bg-linear-to-t from-black/80 via-black/40 to-transparent
                   ${shouldShowOverlay ? 'opacity-100' : 'opacity-0'} transition-opacity duration-200 z-20`}
      >
        <h3 className="text-sm font-bold text-white line-clamp-2 mb-1.5 pr-14">
          {title}
        </h3>
        <Badge variant="outline" className="text-white border-white/30 text-xs w-fit">
          {prettyCondition}
        </Badge>
      </div>
      </div>
    </a>
  );
}
